import { Component, OnInit, computed, signal, inject, ChangeDetectionStrategy } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MatButton } from "@angular/material/button";
import {MatCard, MatCardTitle} from "@angular/material/card";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatTableModule } from "@angular/material/table";
import { MatTooltip } from "@angular/material/tooltip";
import { take } from "rxjs";
import { TourAnchorMatMenuDirective } from "ngx-ui-tour-md-menu";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";

import { LorenAutomationPlanResolverConstant } from "../../resolvers/constants/loren-automation-plans-resolver.constants";
import { LorenPrivilegeEntityType } from "../../../loren-roles/interfaces/loren-role.interface";
import { LorenAutomationPlan } from "../../interfaces/loren-automation-plan.interface";
import { LorenSharedDirectivesModule } from "../../../../shared/directives/loren-shared-directives.module";
import { LorenPageContentComponent } from "../../../../shared/modules/loren-page-content/loren-page-content.component";
import { LorenNavService } from "../../../../shared/modules/loren-nav/components/loren-nav/services/loren-nav-service/loren-nav.service";
import { LorenAuthenticationService } from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { LorenBreakpointObserverService } from "../../../../shared/services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenAutomationPlanService } from "../../services/loren-automation-plan-service/loren-automation-plan.service";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenAutomationPlanUtilsService } from "../../services/loren-automation-plan-utils-service/loren-automation-plan-utils.service";
import { LorenPlanTimePeriodConverterPipe } from "../../pipes/loren-plan-time-period-converter-pipe/loren-plan-time-period-converter.pipe";
import { LorenAutomationPlanDefineComponent } from "../loren-automation-plan-define/loren-automation-plan-define.component";
import { LorenBaseEntityCrudFeatureWithErrorHandlingDirective } from "../../../../shared/base-components/loren-base-entity-crud-feature/loren-base-entity-crud-feature-with-error-handling.directive";
import { LorenErrorHandlingService } from "../../../../shared/services/loren-error-handling-service/loren-error-handling-service.service";
import { LOREN_ROUTES } from "../../../../shared/constants/loren-routes.constants";
import { LorenAutomationPlanTourSteps } from "../../../../shared/constants/loren-tour-steps.constants";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    imports: [
        FormsModule,
        MatButton,
        MatCard,
        MatFormField,
        MatIcon,
        MatInput,
        MatLabel,
        MatTableModule,
        MatTooltip,
        TourAnchorMatMenuDirective,
        TranslocoPipe,
        LorenSharedDirectivesModule,
        LorenAutomationPlanDefineComponent,
        LorenPageContentComponent,
        MatSlideToggle,
        MatCardTitle
    ],
  selector: "loren-automation-plan-all",
  styleUrls: ["./loren-automation-plan-all.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-automation-plan-all.component.html",
})
export class LorenAutomationPlanAllComponent
  extends LorenBaseEntityCrudFeatureWithErrorHandlingDirective<LorenAutomationPlan>
  implements OnInit
{
  protected activatedRoute: ActivatedRoute;
  protected transloco = inject(TranslocoService);
  protected router = inject(Router);
  protected breakpointObserverService: LorenBreakpointObserverService;
  protected lorenNavService: LorenNavService;
  protected assetService = inject(LorenAssetService);
  protected authenticationService = inject(LorenAuthenticationService);
  protected automationPlanService = inject(LorenAutomationPlanService);
  private planTimePeriodConverterPipe = inject(LorenPlanTimePeriodConverterPipe);
  private errorHandlingService = inject(LorenErrorHandlingService);
  private authService = inject(LorenAuthenticationService);

  readonly privileges = LorenPrivilegeEntityType;
  readonly displayedColumns: string[] = [
    "name",
    "conditions",
    "actions",
    "period",
    "type",
    "status",
    "edit",
  ];
  readonly mainAnchorId = this.getAnchorId("ap.all_plans_container");
  readonly createPlanAnchorId = this.getAnchorId("ap.create_plan_create");

  private userId: string;

  searchShown = signal(false);
  tableEntitiesSignal = signal(null);
  createEditPlanForm = signal<boolean>(false);
  filteredEntities = this.entities;

  searchValue = "";

  private assetsError = signal<boolean>(false);
  private geozonesError = signal<boolean>(false);
  private plansError = signal<boolean>(false);
  anyError = computed<boolean>(
    () =>
      this.assetsError() ||
      this.geozonesError() ||
      this.plansError() ||
      this.error
  );

  constructor() {
    super(LorenAutomationPlanResolverConstant.PLANS);
  }

  ngOnInit() {
    super.ngOnInit();
    this.userId = this.authService.getUserInfo().id;
    this.filteredEntities = this.entities;
    this.activatedRoute.data.subscribe((x) => {
      this.createEditPlanForm.set(x.define || false);
    });
    this.transformEntitiesToTableEntities();
  }

  handleSearch() {
    this.filteredEntities = this.searchValue
      ? this.entities?.filter(
          (x) =>
            x.name.toLowerCase().includes(this.searchValue.toLowerCase()) ||
            this.getConditions(
              x.binaryConditions,
              x.geofenceConditions,
              x.numericConditions,
              x.logicalOperator
            )
              .toLowerCase()
              .includes(this.searchValue.toLowerCase())
        )
      : this.entities;
    this.transformEntitiesToTableEntities();
  }

  clearSearch() {
    this.searchValue = "";
    this.handleSearch();
  }

  toggleElementStatus(entityId) {
    const entity = this.entities?.find((e) => e.id === entityId);
    if (!entity) {
      this.errorHandlingService.showSnackbarError();
      return;
    }
    entity.enabled = !entity.enabled;
    this.automationPlanService
      .updatePlan(entityId, entity)
      .pipe(take(1))
      .subscribe({
        error: () => this.errorHandlingService.showSnackbarError(),
      });
  }

  showSearch() {
    this.searchShown.set(true);
  }
  hideSearch() {
    this.clearSearch();
    this.searchShown.set(false);
  }

  showCreateEditPlanForm(item) {
    this.router.navigateByUrl(
      item
        ? `${LOREN_ROUTES.PLANS}/${item.id}/${LOREN_ROUTES.EDIT}`
        : LOREN_ROUTES.PLANS_.define()
    );
  }

  hideCreateEditPlanForm() {
    this.router.navigateByUrl(LOREN_ROUTES.PLANS);
    this.automationPlanService.getAllPlans(this.userId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            this.plansError.set(false);
            this.entities = res.items;
            this.filteredEntities = this.entities;
            this.transformEntitiesToTableEntities();
          },
          error: () => this.plansError.set(true),
        });
  }

  private transformEntitiesToTableEntities() {
    this.tableEntitiesSignal.set(
      this.filteredEntities?.map((e) => {
        const actions = this.getActions(e.actions, e.sound);
        const conditions = this.getConditions(
          e.binaryConditions,
          e.geofenceConditions,
          e.numericConditions,
          e.logicalOperator
        );
        const period = this.getPeriod(e.effectivePeriod);

        return {
          actions,
          conditions,
          period,
          name: e.name,
          status: e.enabled,
          isCompanyLevel: e.isCompanyLevel,
          isEditable: e.isEditable,
          id: e.id,
          item: e,
        };
      })
    );
  }

  private getConditions(
    binaryConditions,
    geofenceConditions,
    numericConditions,
    logicalOperator
  ) {
    const conditionsTexts = [
      ...binaryConditions,
      ...geofenceConditions,
      ...numericConditions,
    ].map((c) =>
      LorenAutomationPlanUtilsService.getConditionText(
        c,
        this.allData.assets,
        this.allData.geozones,
        this.transloco
      )
    );
    if (conditionsTexts.includes(0)) {
      return this.transloco.translate(
        "automation_plans_table.issue_with_condition"
      );
    }
    return conditionsTexts.join(
      ` ${this.transloco
        .translate(`automation_plans.${logicalOperator.toLowerCase()}`)
        .toUpperCase()} `
    );
  }

  private getActions(actions, sound) {
    const allActions = actions.map((a) =>
      this.transloco.translate(
        LorenAutomationPlanUtilsService.getActionDescription(a.action)
      )
    );
    if (sound) {
      allActions.push(this.transloco.translate("automation_plans_form.sound"));
    }
    return allActions.join(
      ` ${this.transloco.translate("automation_plans.and")} `
    );
  }

  private getPeriod(period) {
    const daysAvailable = Object.entries(period.daysOfWeek)
      .filter((e) => e[1])
      .map((e) => e[0]);
    const days =
      daysAvailable.length === 7
        ? this.transloco.translate("days_of_week.everyday")
        : daysAvailable.map((d) =>
            this.transloco.translate(`days_of_week.${d.toLowerCase()}`)
          );
    const timePeriod = `${this.planTimePeriodConverterPipe.transform(
      period.startTime
    )} - ${this.planTimePeriodConverterPipe.transform(period.endTime)}`;
    return { days, timePeriod };
  }

  private getAnchorId(id: string) {
    return LorenAutomationPlanTourSteps.find((x) => x.anchorId === id).anchorId;
  }
}
